import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronLeft, Users, UserPlus, UserCheck, CheckCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { roleLabels } from '../../lib/roleLabels';

const cardStyle = {
  backgroundColor: 'var(--card-bg)',
  border: '1px solid var(--card-border)',
};


const headerStyle = {
  backgroundColor: 'var(--header-bg)',
  borderBottom: '1px solid rgba(255,255,255,0.1)',
  paddingTop: 'var(--sat)',
};

const skeletonBg = 'var(--skeleton-bg)';

const PAGE_SIZE = 50;

// Both tabs return the same shape, so one RPC name lookup is all that differs.
const RPC_FOR = {
  following: 'get_my_following',
  followers: 'get_my_followers',
};

// ── Row (module scope — in-render definitions cause remount bugs) ──

const PersonRow = ({ person: p, isFirst, onOpen, onToggleFollow, busy }) => {
  const name = (p.use_handle && p.handle) ? p.handle : p.display_name;

  return (
    <div
      className="w-full flex items-center gap-3 px-4 py-3"
      style={{ borderTop: isFirst ? 'none' : '1px solid var(--card-border)' }}
    >
      <button
        onClick={() => onOpen(p)}
        className="flex items-center gap-3 flex-1 min-w-0 text-left transition-all active:scale-[0.99]"
      >
        {p.avatar ? (
          <img src={p.avatar} alt="" className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
        ) : (
          <div className="w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center text-white text-sm font-bold bg-gradient-to-br from-brand-primary to-brand-secondary">
            {(name || 'D')[0].toUpperCase()}
          </div>
        )}

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 min-w-0">
            <span className="text-sm font-semibold text-primary truncate">{name}</span>
            {p.is_verified && (
              <CheckCircle size={13} className="flex-shrink-0" style={{ color: 'var(--brand-primary)' }} />
            )}
          </div>
          <p className="text-xs text-tertiary truncate">
            {p.handle && !p.use_handle ? `@${p.handle}` : (roleLabels[p.job_role] || p.job_role || '')}
          </p>
        </div>
      </button>

      <button
        onClick={() => onToggleFollow(p)}
        disabled={busy}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold flex-shrink-0 transition-all hover:brightness-110 active:scale-[0.98] disabled:opacity-50"
        style={
          p.i_follow
            ? { backgroundColor: 'var(--pill-bg)', border: '1px solid var(--pill-border)', color: 'var(--brand-primary)' }
            : { backgroundColor: 'var(--brand-primary)', border: '1px solid var(--brand-primary)', color: '#fff' }
        }
      >
        {p.i_follow ? <UserCheck size={13} /> : <UserPlus size={13} />}
        {p.i_follow ? 'Following' : 'Follow'}
      </button>
    </div>
  );
};

const RowSkeleton = ({ isFirst }) => (
  <div className="flex items-center gap-3 px-4 py-3 animate-pulse" style={{ borderTop: isFirst ? 'none' : '1px solid var(--card-border)' }}>
    <div className="w-10 h-10 rounded-full flex-shrink-0" style={{ backgroundColor: skeletonBg }} />
    <div className="flex-1 space-y-2">
      <div className="h-3 rounded w-1/3" style={{ backgroundColor: skeletonBg }} />
      <div className="h-2.5 rounded w-1/4" style={{ backgroundColor: skeletonBg }} />
    </div>
  </div>
);

const TabButton = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className="px-3.5 py-1.5 rounded-lg text-sm font-semibold transition-all active:scale-[0.98]"
    style={active
      ? { backgroundColor: 'var(--pill-bg)', border: '1px solid var(--pill-border)', color: 'var(--brand-primary)' }
      : { backgroundColor: 'transparent', border: '1px solid transparent', color: 'var(--text-tertiary)' }}
  >
    {children}
  </button>
);

// ── Main ────────────────────────────────────────────────────

const ConnectionsScreen = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Tab lives in the URL so back/forward and deep links work
  const tab = searchParams.get('tab') === 'followers' ? 'followers' : 'following';

  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const fetchPage = useCallback(async (which, offset) => {
    const { data, error: rpcError } = await supabase.rpc(RPC_FOR[which], {
      p_limit: PAGE_SIZE,
      p_offset: offset,
    });
    if (rpcError) {
      console.error(`${RPC_FOR[which]}:`, rpcError);
      return null;
    }
    return data ?? [];
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    (async () => {
      const data = await fetchPage(tab, 0);
      if (cancelled) return;
      if (data === null) {
        setError(true);
      } else {
        setPeople(data);
        setHasMore(data.length === PAGE_SIZE);
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [tab, fetchPage]);

  const fetchMore = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    const data = await fetchPage(tab, people.length);
    if (data !== null) {
      setPeople(prev => [...prev, ...data]);
      setHasMore(data.length === PAGE_SIZE);
    }
    setLoadingMore(false);
  };

  // Optimistic. The row stays in place after unfollowing rather than vanishing —
  // a row disappearing under the finger makes mis-taps unrecoverable, and
  // leaving it lets the user undo. It's gone on the next visit.
  const handleToggleFollow = async (p) => {
    if (busyId) return;
    setBusyId(p.person_id);
    const next = !p.i_follow;
    setPeople(prev => prev.map(x => (x.person_id === p.person_id ? { ...x, i_follow: next } : x)));

    const { data, error: rpcError } = await supabase.rpc('toggle_follow_user', { p_followee_id: p.person_id });
    setBusyId(null);

    if (rpcError) {
      console.error('toggle_follow_user:', rpcError);
      setPeople(prev => prev.map(x => (x.person_id === p.person_id ? { ...x, i_follow: !next } : x)));
    } else {
      setPeople(prev => prev.map(x => (x.person_id === p.person_id ? { ...x, i_follow: data === true } : x)));
    }
  };

  const emptyCopy = tab === 'following'
    ? { title: "You're not following anyone yet", sub: 'Follow DSPs from their profile to see their posts in your Following feed.' }
    : { title: 'No followers yet', sub: 'As people follow you, they\'ll show up here.' };

  return (
    <div className="min-h-screen pb-nav">
      {/* Mobile sticky header */}
      <div className="sticky top-0 z-20 md:hidden" style={headerStyle}>
        <div className="max-w-2xl mx-auto px-4 py-2 flex items-center gap-2">
          <button onClick={() => navigate(-1)} className="p-1 -ml-1 transition-all hover:scale-110 active:scale-95">
            <ChevronLeft size={22} className="text-white" />
          </button>
          <h1 className="text-lg font-bold text-white">Connections</h1>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-4">
        {/* Desktop header replacement */}
        <div className="hidden md:flex items-center justify-between mb-4">
          <h1 className="text-xl font-bold text-primary">Connections</h1>
        </div>

        <div className="flex items-center gap-1 mb-3">
          <TabButton active={tab === 'following'} onClick={() => setSearchParams({ tab: 'following' })}>Following</TabButton>
          <TabButton active={tab === 'followers'} onClick={() => setSearchParams({ tab: 'followers' })}>Followers</TabButton>
        </div>

        {loading ? (
          <div className="rounded-2xl overflow-hidden" style={cardStyle}>
            <RowSkeleton isFirst />
            <RowSkeleton />
            <RowSkeleton />
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              Couldn't load your connections. Pull to refresh or try again later.
            </p>
          </div>
        ) : people.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-16 text-center">
            <Users size={32} style={{ color: 'var(--text-tertiary)' }} strokeWidth={1.5} />
            <p className="text-sm font-medium text-tertiary">{emptyCopy.title}</p>
            <p className="text-xs text-tertiary opacity-60">{emptyCopy.sub}</p>
          </div>
        ) : (
          <>
            <div className="rounded-2xl overflow-hidden" style={cardStyle}>
              {people.map((p, i) => (
                <PersonRow
                  key={p.person_id}
                  person={p}
                  isFirst={i === 0}
                  onOpen={(x) => navigate(`/profile/${x.person_id}`)}
                  onToggleFollow={handleToggleFollow}
                  busy={busyId === p.person_id}
                />
              ))}
            </div>

            {hasMore && (
              <div className="flex justify-center mt-4">
                <button
                  onClick={fetchMore}
                  disabled={loadingMore}
                  className="px-4 py-2 rounded-lg text-sm font-semibold transition-all hover:brightness-110 active:scale-[0.98] disabled:opacity-50"
                  style={{ backgroundColor: 'var(--pill-bg)', color: 'var(--brand-primary)', border: '1px solid var(--pill-border)' }}
                >
                  {loadingMore ? 'Loading…' : 'Load more'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ConnectionsScreen;
